import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import moment from "moment-timezone";
import AmplifyAPIWrapper from "../../API/AmplifyAPIWrapper";

interface IStatusMsg {
  severity: "error" | "warning" | "success" | "info";
  msg: string;
}
export interface IHotelBookingState {
  hotelBookings: any[];
  timeZone: string | null;
  hotelBookingsCounts: {
    additions: number;
    removals: number;
    noChanges: number;
    modifications: number;
    canceled: number;
  };
  statusMsg: IStatusMsg | null;
  jwtDecoded: any;
  parsedToken: boolean;
  filter: string;
  fetchingThunk: boolean;
  fetchedThunk: boolean;
}

const initialState: IHotelBookingState = {
  hotelBookings: [],
  timeZone: null,
  hotelBookingsCounts: {
    additions: 0,
    removals: 0,
    noChanges: 0,
    modifications: 0,
    canceled: 0,
  },
  statusMsg: null,
  jwtDecoded: {},
  parsedToken: false,
  filter: "",
  fetchingThunk: false,
  fetchedThunk: false,
};

const getZoneFromOffset = (offsetString: string): string[] =>
  moment.tz.names().filter((tz) => moment.tz(tz).format("Z") === offsetString);

//Get Bookings
export const getHotelBookingsThunk = createAsyncThunk(
  "hotelbookings/getHotelBookings",
  async (hotel: { hotelIDs: string[]; start?: number; end?: number }, { rejectWithValue }) => {
    const hotelIDList = hotel.hotelIDs.join(",");
    const queryString = hotel.start && hotel.end ? `&start=${hotel.start}&end=${hotel.end}` : "";
    const res = await AmplifyAPIWrapper.Get(`/hotels/bookings?&hotelIds=${hotelIDList}${queryString}`);
    return res;
  }
);

//Confirm Booking
export const confirmBooking = createAsyncThunk(
  "hotels/confirmBooking",
  async (hotel: { booking: any; hotelID: string }) => {
    const method = hotel.booking.hasOwnProperty("_id") ? "PUT" : "POST";
    const res =
      method === "PUT"
        ? await AmplifyAPIWrapper.Put(`/hotels/bookings/${hotel.booking._id}?hotelIds=${hotel.hotelID}`, hotel.booking)
        : await AmplifyAPIWrapper.Post(`/hotels/bookings?hotelIds=${hotel.hotelID}`, hotel.booking);
    if (res) {
      return res;
    } else {
      return null;
    }
  }
);

//Cancel Booking
export const cancelBooking = createAsyncThunk(
  "hotelbookings/cancelBooking",
  async (hotel: { booking: any; hotelIDs: string[] }, getState) => {
    const hotelIDList = hotel.hotelIDs.join(",");
    const queryString = `?hotelIds=${hotelIDList}`;
    const res = await AmplifyAPIWrapper.Put(`/hotels/bookings/${hotel.booking._id}${queryString}`, hotel.booking);
    if (res) {
      return res;
    } else {
      return null;
    }
  }
);

//No Availability
export const BookingNotAvailable = createAsyncThunk(
  "hotelbookings/BookingNotAvailable",
  async (hotel: { booking: any; hotelIDs: string[]; start?: number; end?: number }, getState) => {
    const hotelIDList = hotel.hotelIDs.join(",");
    const queryString = `?hotelIds=${hotelIDList}`;
    const method = hotel.booking.hasOwnProperty("_id") ? "PUT" : "POST";
    const url = hotel.booking.hasOwnProperty("_id")
      ? `/hotels/bookings/${hotel.booking._id}${queryString}`
      : `/hotels/bookings${queryString}`;
    const res =
      method === "PUT"
        ? await AmplifyAPIWrapper.Put(url, hotel.booking)
        : await AmplifyAPIWrapper.Post(url, hotel.booking);
    if (res) {
      return res;
    } else {
      return null;
    }
  }
);

//validate modification
export const validateBooking = createAsyncThunk(
  "hotelbookings/validateBooking",
  async (hotel: { booking: any; hotelIDs: string[] }, getState) => {
    const hotelIDList = hotel.hotelIDs.join(",");
    const queryString = `?hotelIds=${hotelIDList}`;
    hotel.booking.status = "modifications";
    const res = await AmplifyAPIWrapper.Put(`/hotels/bookings/${hotel.booking._id}${queryString}`, hotel.booking);
    if (res) {
      return res;
    } else {
      return null;
    }
  }
);
//no show booking
export const noShowBooking = createAsyncThunk(
  "hotelbookings/noShowBooking",
  async (hotel: { booking: any; hotelIDs: string[] }, getState) => {
    const hotelIDList = hotel.hotelIDs.join(",");
    const queryString = `?hotelIds=${hotelIDList}`;
    delete hotel.booking.id; //This is a row used for datagrid, not the record.
    const res = await AmplifyAPIWrapper.Put(`/hotels/bookings/${hotel.booking._id}${queryString}`, hotel.booking);
    if (res) {
      return res;
    } else {
      return null;
    }
  }
);

const getStatusKeys = (
  statusName: string
): { key: string; currentStatusLabel: string; newStatus: string; newStatusLabel: string } => {
  let key = "";
  let currentStatusLabel = "";
  let newStatusLabel = "";
  let newStatus = "";
  switch (statusName.toLowerCase()) {
    case "additions":
    case "confirm":
      currentStatusLabel = "Confirm";
      newStatus = "noChanges";
      newStatusLabel = "N/A";
      key = "cur";
      break;
    case "canceled":
      currentStatusLabel = "Canceled";
      newStatus = "noChanges";
      newStatusLabel = "N/A";
      key = "prev";
      break;
    case "modifications":
    case "validate":
      currentStatusLabel = "Validate";
      newStatus = "noChanges";
      newStatusLabel = "N/A";
      key = "prev";
      break;
    case "removals":
    case "delete":
      currentStatusLabel = "Delete";
      newStatus = "noChanges";
      newStatusLabel = "N/A";
      key = "prev";
      break;
    case "nochanges":
    case "n/a":
    case "":
      currentStatusLabel = "N/A";
      newStatus = "noChanges";
      newStatusLabel = "N/A";
      key = "prev";
      break;
  }
  return { key, currentStatusLabel, newStatus, newStatusLabel };
};

const flattenRawBookings = (rawBookings: { [x: string]: any[] }): any[] => {
  let bookingRows: any[] = [];
  Object.keys(rawBookings).map((statusName: string) => {
    const { key, currentStatusLabel } = getStatusKeys(statusName);
    if (typeof rawBookings[statusName] === "object") {
      const bookingRowsinStatus = rawBookings[statusName].map((booking: any) => {
        const temp_id = uuidv4();
        return {
          _id: booking[key]?._id ?? "",
          id: uuidv4(), //datagrid wants this
          hotelName: booking[key].hotelName,
          temp_id: booking[key]?.temp_id ?? temp_id,
          crew1: booking[key].crew1,
          checkInLocal: booking[key].checkInLocal,
          checkOutLocal: booking[key].checkOutLocal,
          checkInTimeLocal: booking[key].checkInTimeLocal,
          checkOutTimeLocal: booking[key].checkOutTimeLocal,
          checkOutDateLocal: booking[key].checkOutDateLocal,
          checkInDateLocal: booking[key].checkInDateLocal,
          description: booking[key].description,
          hotelICAO: booking[key].hotelICAO,
          cancelationCode: booking[key].cancelationCode ?? "",
          confirmationCode: booking[key].confirmationCode ?? "",
          status: currentStatusLabel,
          statusName: statusName,
          hotel_id: booking[key].hotel_id,
          pairing_id: booking[key].pairing_id,
          crew_id: booking[key].crew_id,
          checkInUTC: booking[key].checkInUTC,
          checkOutUTC: booking[key].checkOutUTC,
          isNoShow: booking[key].isNoShow,
          noHotelAvailability: booking[key].noHotelAvailability,
          inboundFlightNumber:
            booking[key].inboundFlightNumber.toUpperCase() === "UNDEFINED" ? "" : booking[key].inboundFlightNumber,
          outboundFlightNumber:
            booking[key].outboundFlightNumber.toUpperCase() === "UNDEFINED" ? "" : booking[key].outboundFlightNumber,
          changes: booking[key].changes ?? {},
        };
      });
      bookingRows = [...bookingRows, ...bookingRowsinStatus];
    }
  });
  return bookingRows;
};

// const setTimezone = (state: IHotelBookingState, checkInLocal: string): void => {
//   const timezoneOffset = (tz: string): string => {
//     const lastIdx = tz.lastIndexOf("-");
//     return `-${tz.slice(lastIdx + 1)}`;
//   };
//   const timezone = getZoneFromOffset(timezoneOffset(checkInLocal));
//   state.timeZone = timezone[0];
// };

const sortRows = (dataGridrows: any[]): any[] => {
  return dataGridrows.sort((a, b): any => {
    if (a.description < b.description) {
      return -1;
    }
    if (b.description < a.description) {
      return 1;
    }
    return 0;
  });
};

export const HotelBookings: any = createSlice({
  name: "hotelBookingPortal",
  initialState,
  reducers: {
    updateStatusMsg: (state, action: PayloadAction<IStatusMsg>) => {
      state.statusMsg = action.payload;
    },
    setFilter: (state, action: PayloadAction<any>) => {
      state.filter = action.payload;
    },
    setFetchingThunk: (state, action: PayloadAction<boolean>) => {
      state.fetchingThunk = action.payload;
    },
    updateHotelRow: (state, action: any) => {
      const { key, currentStatusLabel, newStatus, newStatusLabel } = getStatusKeys(action.data?.status ?? "");
      const curBookingIdx = state.hotelBookings.findIndex((oldBooking: any) => {
        if (action.data.status === "Confirm") {
          return action.data.temp_id === oldBooking.temp_id;
        } else {
          return action.data._id === oldBooking._id;
        }
      });
      action.data.status = newStatusLabel;
      action.data.statusName = newStatus;
      action.data.id = uuidv4(); //Just so datagrid doesnt barf.
      if (!action.data.hasOwnProperty("changes")) {
        action.data.changes = {};
      }
      //Clean up some of the result.
      action.data.inboundFlightNumber =
        action.data.inboundFlightNumber.toUpperCase() === "UNDEFINED" ? "" : action.data.inboundFlightNumber;
      action.data.outboundFlightNumber =
        action.data.outboundFlightNumber.toUpperCase() === "UNDEFINED" ? "" : action.data.outboundFlightNumber;

      console.log("new row", action.data);
      state.hotelBookings[curBookingIdx] = action.data;

      //change counts.
      state.hotelBookingsCounts.additions = state.hotelBookings.filter((b) => b.statusName === "additions").length;
      state.hotelBookingsCounts.modifications = state.hotelBookings.filter(
        (b) => b.statusName === "modifications"
      ).length;
      state.hotelBookingsCounts.noChanges = state.hotelBookings.filter((b) => b.statusName === "noChanges").length;
      state.hotelBookingsCounts.removals = state.hotelBookings.filter((b) => b.statusName === "removals").length;
      state.hotelBookingsCounts.canceled = state.hotelBookings.filter((b) => b.statusName === "canceled").length;
    },
  },
  extraReducers(builder) {
    builder
      // Get bookings list
      .addCase(getHotelBookingsThunk.pending, (state) => {
        state.fetchedThunk = false;
        state.fetchingThunk = true;
        state.statusMsg = { severity: "info", msg: "Loading Bookings..." };
      })
      .addCase(getHotelBookingsThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.statusMsg = null;
        state.hotelBookings = flattenRawBookings(action.payload);
        state.hotelBookingsCounts.additions = action.payload?.additions.length ?? 0;
        state.hotelBookingsCounts.modifications = action.payload?.modifications.length ?? 0;
        state.hotelBookingsCounts.noChanges = action.payload?.noChanges.length ?? 0;
        state.hotelBookingsCounts.removals = action.payload?.removals.length ?? 0;
        state.hotelBookingsCounts.canceled = action.payload?.canceled.length ?? 0;
        state.fetchedThunk = true;
        state.fetchingThunk = false;
      })
      .addCase(getHotelBookingsThunk.rejected, (state) => {
        state.fetchedThunk = false;
        state.fetchingThunk = false;
        state.statusMsg = {
          severity: "error",
          msg: "Failed to load bookings, please contact support for assistance",
        };
      })
      //Confirm booking
      .addCase(confirmBooking.pending, (state, action) => {
        state.statusMsg = {
          severity: "info",
          msg: "Committing Changes...",
        };
        state.fetchedThunk = false;
        state.fetchingThunk = true;
      })
      .addCase(confirmBooking.fulfilled, (state, action) => {
        HotelBookings.caseReducers.updateHotelRow(state, { data: action.payload, newStatusName: "noChanges" });
        state.statusMsg = { severity: "success", msg: "Confirmation Saved." };
        state.fetchedThunk = true;
        state.fetchingThunk = false;
      })
      .addCase(confirmBooking.rejected, (state, action) => {
        state.fetchedThunk = false;
        state.fetchingThunk = false;
        state.statusMsg = {
          severity: "error",
          msg: "Failed to save changes.",
        };
      })
      //Cancel booking
      .addCase(cancelBooking.pending, (state, action) => {
        state.statusMsg = {
          severity: "info",
          msg: "Committing Changes...",
        };
        state.fetchedThunk = false;
        state.fetchingThunk = true;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        HotelBookings.caseReducers.updateHotelRow(state, { data: action.payload, statusName: "noChanges" });
        state.statusMsg = { severity: "success", msg: "Cancelation Saved." };
        state.fetchedThunk = true;
        state.fetchingThunk = false;
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        state.fetchedThunk = false;
        state.fetchingThunk = false;
        state.statusMsg = {
          severity: "error",
          msg: "Failed to save changes. ",
        };
      })
      //Deny booking (no availability)
      .addCase(BookingNotAvailable.rejected, (state, action) => {
        state.fetchedThunk = false;
        state.fetchingThunk = false;
        state.statusMsg = {
          severity: "error",
          msg: "Failed to save changes. ",
        };
      })
      .addCase(BookingNotAvailable.pending, (state, action) => {
        state.statusMsg = {
          severity: "info",
          msg: "Committing Changes...",
        };
        state.fetchedThunk = false;
        state.fetchingThunk = true;
      })
      .addCase(BookingNotAvailable.fulfilled, (state, action) => {
        HotelBookings.caseReducers.updateHotelRow(state, { data: action.payload, statusName: "noChanges" });
        if (action.meta.arg.booking.noHotelAvailability) {
          state.statusMsg = {
            severity: "success",
            msg: "Booking successfully marked as unavailable.",
          };
        } else {
          state.statusMsg = {
            severity: "success",
            msg: "Booking availability status has been reset successfully",
          };
        }

        state.fetchedThunk = true;
        state.fetchingThunk = false;
      })
      //no show booking
      .addCase(noShowBooking.rejected, (state, action) => {
        state.fetchedThunk = false;
        state.fetchingThunk = false;
        state.statusMsg = {
          severity: "error",
          msg: "Failed to save changes.",
        };
      })
      .addCase(noShowBooking.pending, (state, action) => {
        state.statusMsg = {
          severity: "info",
          msg: "Committing Changes...",
        };
        state.fetchedThunk = false;
        state.fetchingThunk = true;
      })
      .addCase(noShowBooking.fulfilled, (state, action: PayloadAction<any>) => {
        HotelBookings.caseReducers.updateHotelRow(state, { data: action.payload, statusName: "noChanges" });
        if (action.payload.isNoShow === "true") {
          state.statusMsg = {
            severity: "success",
            msg: "Booking marked as a no-show, successfully.",
          };
        } else {
          state.statusMsg = {
            severity: "success",
            msg: "Booking no-show status has been reset successfully",
          };
        }
        state.fetchedThunk = true;
        state.fetchingThunk = false;
      }) //validateBooking
      .addCase(validateBooking.rejected, (state, action) => {
        state.fetchedThunk = false;
        state.fetchingThunk = false;
        state.statusMsg = {
          severity: "error",
          msg: "Failed to save changes. ",
        };
      })
      .addCase(validateBooking.pending, (state, action) => {
        state.statusMsg = {
          severity: "info",
          msg: "Validating Changes...",
        };
        state.fetchedThunk = false;
        state.fetchingThunk = true;
      })
      .addCase(validateBooking.fulfilled, (state, action) => {
        HotelBookings.caseReducers.updateHotelRow(state, { data: action.payload, statusName: "noChanges" });

        state.statusMsg = {
          severity: "success",
          msg: "Booking changes acknowledged.",
        };

        state.fetchedThunk = true;
        state.fetchingThunk = false;
      });
  },
});

export const { updateStatusMsg, setFilter, setFetchingThunk } = HotelBookings.actions;

export default HotelBookings.reducer;
